import { useState, useEffect, useCallback, useMemo } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import PersonIcon from "@mui/icons-material/Person";
import DeleteIcon from "@mui/icons-material/Delete";
import BuildIcon from "@mui/icons-material/Build";
import DateRangeIcon from "@mui/icons-material/DateRange";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import ApprovalIcon from "@mui/icons-material/Approval";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import TagIcon from "@mui/icons-material/Tag";
import DownloadIcon from "@mui/icons-material/Download";
import MailIcon from "@mui/icons-material/Mail";
import PreviewIcon from "@mui/icons-material/Preview";
import ConstructionIcon from "@mui/icons-material/Construction";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import RecyclingIcon from "@mui/icons-material/Recycling";
import BusinessIcon from "@mui/icons-material/Business";

import { useNavigate } from "react-router-dom";
import Translate from "../../utils/Translate";
import { formatDateRange, formatDateWithMonthOffSet } from "../../utils/dateTimeFormat";
import Api, { useApi } from "../../utils/Api";
import DotMenu from "../DotMenu";
import YesOrNoDialog from "../YesOrNoDialog";
import { useSnackbar } from "notistack";
import download from "downloadjs";
import { CustomNoRowsOverlay } from "../../utils/StyledGridOverlay";
import { IconAndLabelColumnHeader, TableAvatar2 } from "../../utils/dataGrid";
import PermitCard from "../actor-permits/PermitCard";
import {
  permitStatusToSortableValue,
  renderPermitStatus,
  formatPermitStatus,
} from "../actor-permits/utils/permitStatus";
import { dataGridLang } from "../MemToolBar";
import { ToolBar } from "../ToolBar";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import ActorPermitFlowDialogContainer from "../actor-permits/ActorPermitFlowDialogContainer";
import ActorPermitFlowDialogHeader from "../actor-permits/ActorPermitFlowDialogHeader";
import usePagination from "../../utils/pagination";
import DataGridEasyFilters from "../DataGridEasyFilters";
import InactivateActorPermitDialog from "../actor-permits/InactivateActorPermitDialog";
import { Block, CheckCircle } from "@mui/icons-material";

const getDotMenuActions = ({
  actorPermit,
  onInactivateActorPermit,
  onDeleteActorPermitPermit,
  onSendPermitSignReminder,
  handleMarkPermitAsReviewed,
}) => {
  const manualPermit = actorPermit.blobFile && true;
  const isSignedByBoth = actorPermit.isSignedByResponsible && actorPermit.isSignedByStudent;

  // körtillståndet är giltigt ifall validTo är null eller har passerat
  const isActive = !actorPermit.validTo || new Date(actorPermit.validTo) > new Date();

  // för att kunna tas bort ska körtillståndet vara inaktivt och inte signerat av båda, manuella körtilstånd signeras inte
  const deletePossible = !isActive || (!manualPermit && !isSignedByBoth);
  const inactivatePossible = !deletePossible && isActive;

  return [
    {
      icon: <MailIcon />,
      disabled: isSignedByBoth,
      text: Translate.get("ResendSignarureRequest"),
      onClick: () => onSendPermitSignReminder(actorPermit.actorPermitGroupId, actorPermit.actorPermitId),
      hide: actorPermit.blobFile !== null,
    },
    {
      icon: <Block />,
      disabled: !inactivatePossible,
      text: Translate.get("InactivatePermit"),
      onClick: () => {
        onInactivateActorPermit(actorPermit);
      },
    },
    {
      icon: <PreviewIcon />,
      text: Translate.get("MarkAsReviewed"),
      onClick: () => handleMarkPermitAsReviewed(actorPermit.actorPermitId),
    },
    {
      icon: <RecyclingIcon />,
      text: Translate.get("CreateFromThis"),
      dialogHeader: <ActorPermitFlowDialogHeader />,
      component: (
        <ActorPermitFlowDialogContainer
          actorIds={[actorPermit?.actorId]}
          skipStep1={false}
          cloneFromActorPermit={actorPermit}
        />
      ),
      disabled: actorPermit?.blobFile,
    },
    {
      icon: <DeleteIcon />,
      text: Translate.get("DeletePermit"),
      disabled: !deletePossible,
      onClick: () => onDeleteActorPermitPermit(actorPermit.actorPermitId),
    },
  ];
};

function buildPaginationFilter(holderActorId) {
  if (!holderActorId) {
    return null;
  }

  return {
    items: [
      {
        id: 1,
        field: "actorId",
        operator: "=",
        value: holderActorId,
      },
    ],
    logicOperator: "and",
  };
}

const Permits = ({
  searchText,
  actorName,
  actorOrgNo,
  holderActorId,
  cardView = true,
  hideFilter = true,
  defaultSort,
  defaultStdFilter,
  onReload,
  onLoaded,
  setIsLoading,
}) => {
  const [actorPermitToInactivate, setActorPermitToInactivate] = useState(null);
  const [actorPermitIdToDelete, setActorPermitIdToDelete] = useState(null);
  const [paginationKey] = useState(holderActorId ? `HOLDER_${holderActorId}_PERMITS_GRID` : "PERMITS_GRID");
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const api = useApi();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [actorId, setActorId] = useState("");
  const apiRef = useGridApiRef();

  const loadPermits = useCallback(
    async (queryModel) => {
      // Avoid unnecessary call to get-page when navigating to an actor
      if (!setIsLoading) {
        return { rows: [], rowCount: 0 };
      }
      setIsLoading(true);

      try {
        const url = `${import.meta.env.VITE_MAIN_URL}permits/get-page`;
        const response = await api.fetch(url, queryModel, "POST");
        if (onLoaded) {
          onLoaded();
        }
        if (response && response.isSuccessful) {
          // TODO Gör kanske mappningen på servern
          const mappedRows = response.rows.map((permit) => ({
            ...permit,
            parentActorName: permit.parentActorName !== "ePortMaster" ? permit.parentActorName : "",
            responsibleName: permit.responsibleActorId !== 4 ? permit.responsibleActorName : "",
            machineGroup: permit.skillGroupDescription,
            machineTypes: permit.skills?.map((s) => s.shortCode).join(", "),
            isSignedByResponsible: !!permit.responsibleSignatureId,
            isSignedByStudent: !!permit.signatureId,
            orgNo: permit.actorOrgNo || "",
            externalActorNo: permit.externalActorNo || "",
          }));

          return {
            rows: mappedRows,
            rowCount: response.rowCount,
          };
        }
        return { rows: [], rowCount: 0 };
      } finally {
        setIsLoading(false);
      }
    },
    [api, onLoaded, setIsLoading],
  );

  const { localUpdateRows, reloadData, resetPagination, ...pagination } = usePagination(
    loadPermits,
    searchText,
    [
      {
        field: "validTime",
        sort: "desc",
      },
    ],
    null,
    holderActorId ? [10, 20, 30] : [50, 250, 1000],
    paginationKey,
    buildPaginationFilter(holderActorId),
    holderActorId ? true : false,
  );

  useEffect(() => {
    const fetchData = async () => {
      // Avoid unneccesary call to teachers/filter/actorId when navigating to an actor
      if (!setIsLoading) {
        return;
      }
      await getUserActorId();
    };
    fetchData();
  }, []);

  async function getUserActorId() {
    const [filterData] = await Promise.all([
      api.fetch(`${import.meta.env.VITE_MAIN_URL}actors/teachers/filter/actorids`, false, "GET"),
    ]);
    if (filterData && filterData.isSuccessful !== false) {
      setActorId(filterData.actorId);
    }
  }

  const easyFilters = useMemo(
    () => [
      {
        id: "MyPermits",
        name: Translate.get("MyPermits"),
        description: Translate.get("MyPermitsDescription"),
        filterModel: {
          items: [
            {
              id: "MyPermits-responsibleActorId",
              field: "responsibleActorId",
              operator: "=",
              value: actorId,
            },
            {
              id: "MyPermits-actorId",
              field: "actorId",
              operator: "=",
              value: actorId,
            },
          ],
          logicOperator: "or",
        },
      },
      {
        id: "PermitExpires",
        name: Translate.get("PermitExpires"),
        description: Translate.get("PermitExpiresDescription"),
        filterModel: {
          items: [
            {
              id: "PermitExpires-validTo",
              field: "validTo",
              operator: "=",
              value: "3",
            },
          ],
          logicOperator: "and",
        },
      },
      {
        id: "PermitNotFullySigned",
        name: Translate.get("PermitNotFullySigned"),
        description: Translate.get("PermitNotFullySignedDescription"),
        filterModel: {
          items: [
            {
              id: "PermitNotFullySigned-permitNotSigned",
              field: "permitNotSigned",
              operator: "=",
              value: "true",
            },
          ],
          logicOperator: "and",
        },
      },
      {
        id: "ReviewNeeded",
        name: Translate.get("ReviewNeeded"),
        description: Translate.get("ReviewNeededDescription"),
        filterModel: {
          items: [
            {
              id: "ReviewNeeded-reviewIsDue",
              field: "reviewIsDue",
              operator: "is",
              value: "true",
            },
          ],
          logicOperator: "and",
        },
      },
    ],
    [actorId],
  );

  const localizedTextsMap = dataGridLang();

  async function handleReload() {
    // mårten 2025-10-03 lagt till
    await reloadData();

    //mårten 2025-10-03: koden nedan verkar inte ha någon effekt
    if (onReload) {
      await onReload();
    } else {
      navigate(0);
    }
  }

  const downloadCustomPermit = (documentId) => {
    (async () => {
      const data = await api.fetch(`${import.meta.env.VITE_MAIN_URL}permits/file/get/${documentId}`);
      const res = await fetch(data.sasURI);
      const contentType = res.headers.get("Content-Type");
      const raw = await res.blob();
      download(raw, data.filename, contentType);
    })();
  };

  async function handleDownloadActorPermit(actorPermitId, actorName) {
    setLoading(actorPermitId);
    const blob = await Api.fetchBlob(
      `${import.meta.env.VITE_MAIN_URL}permits/pdf/admin?actorPermitId=${actorPermitId}`,
      false,
      "GET",
    );
    const filename = `permit-${actorName.trim().replace(" ", "_")}.pdf`;
    download(blob, filename, blob.type);
    setLoading(false);
  }

  async function handleInactivateActorPermit(inactivateData) {
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}permits/${actorPermitToInactivate.actorPermitId}/inactivate`,
      inactivateData,
      "POST",
    );
    if (response && response.isSuccessful) {
      await handleReload();
    }
    setActorPermitToInactivate(null);
  }

  const navigateToActor = (actorId) => {
    navigate(`/dashboard/person/${actorId}`, { replace: false });
  };

  // Find the selected filter based on the current filter model. Used when coming back from an actor
  const selectedFilter = useMemo(() => {
    if (!pagination.filterModel?.items?.length) {
      return null;
    }
    return easyFilters.find((filter) => {
      const filterItems = filter.filterModel.items;
      const currentItems = pagination.filterModel.items;
      if (filterItems.length !== currentItems.length) {
        return false;
      }
      return filterItems.every((item, index) => {
        const currentItem = currentItems[index];
        return (
          item.field === currentItem.field && item.operator === currentItem.operator && item.value === currentItem.value
        );
      });
    });
  }, [pagination.filterModel, easyFilters]);

  async function handleDeleteActorPermit() {
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}permits/${actorPermitIdToDelete}`,
      true,
      "DELETE",
    );
    if (response && response.isSuccessful) {
      await handleReload();
    }
    setActorPermitIdToDelete(null);
  }

  async function handleMarkPermitAsReviewed(actorPermitId) {
    const reqObj = { actorPermitId };
    const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}permits/review`, reqObj, "POST");
    if (response && response.isSuccessful) {
      await handleReload();
    }
  }

  async function handleSendNextPermitSignEmail(actorPermitGroupId, actorPermitId) {
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}permits/email/next-signer?actorPermitGroupId=${actorPermitGroupId}&actorPermitId=${actorPermitId}`,
      false,
      "GET",
    );
    if (response && response.isSuccessful) {
      const successMessage = enqueueSnackbar(Translate.get("SignReminderSent"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
      await handleReload();
    }
  }

  // Define listViewColumn for small screens
  const listViewColDef = {
    field: "listColumn",
    display: "flex",
    renderCell: (params) => (
      <div style={{ width: "100%" }}>
        <PermitCard
          loading={loading}
          handleDownloadActorPermit={handleDownloadActorPermit}
          downloadCustomPermit={downloadCustomPermit}
          key={params.row.actorPermitId}
          actorName={actorName}
          cardView={true}
          {...params.row}
          extraActions={getDotMenuActions({
            actorPermit: params.row,
            onInactivateActorPermit: setActorPermitToInactivate,
            onDeleteActorPermitPermit: setActorPermitIdToDelete,
            onSendPermitSignReminder: handleSendNextPermitSignEmail,
            handleMarkPermitAsReviewed: handleMarkPermitAsReviewed,
          })}
        />
      </div>
    ),
  };

  // Define columns for table view (non-small screens)
  let columns = [
    {
      field: "orgNo",
      headerName: Translate.get("PersonalNo"),
      hide: true,
      disableExport: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <IconAndLabelColumnHeader icon={FingerprintIcon} label={Translate.get("PersonalNo")} />,
      valueGetter: (value, row) => row.orgNo || "",
    },
    {
      field: "externalActorNo",
      headerName: Translate.get("ExternalActorNo"),
      hide: true,
      disableExport: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <IconAndLabelColumnHeader icon={TagIcon} label={Translate.get("ExternalActorNo")} />,
      valueGetter: (value, row) => row.externalActorNo || "",
    },
    {
      field: "machineGroup",
      headerName: Translate.get("MachineGroup"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={ConstructionIcon} label={Translate.get("MachineGroup")} />,
    },
    {
      field: "machineTypes",
      headerName: Translate.get("MachineType"),
      hide: !cardView,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={BuildIcon} label={Translate.get("MachineType")} />,
    },
    {
      field: "validTime",
      headerName: Translate.get("ValidTime"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={DateRangeIcon} label={Translate.get("ValidTime")} />,
      valueGetter: (value, row, column, apiRef) => formatDateRange(row.validFrom, row.validTo),
    },
    {
      field: "responsibleName",
      headerName: Translate.get("Responsible"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={HowToRegIcon} label={Translate.get("Responsible")} />,
      display: "flex",
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <TableAvatar2
            showNameAndEmailOnly={true}
            name={params.row.responsibleActorName}
            email={params.row.responsibleActorEmail}
          />
        </div>
      ),
    },
    {
      field: "responsibleTitle",
      headerName: Translate.get("ResponsibleTitle"),
      disableColumnMenu: true,
      flex: 1,
      hide: true,
      renderHeader: () => <IconAndLabelColumnHeader icon={HowToRegIcon} label={Translate.get("ResponsibleTitle")} />,
    },
    {
      field: "parentActorName",
      headerName: Translate.get("Company"),
      disableColumnMenu: true,
      sortable: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={BusinessIcon} label={Translate.get("Company")} />,
      valueGetter: (value, row, column, apiRef) => row.parentActorName,
    },
    {
      field: "customPermitStatus",
      headerName: Translate.get("Status"),
      disableColumnMenu: true,
      flex: 0.5,
      minWidth: 100,
      filterable: false,
      sortable: true,
      valueFormatter: (value, row, column, apiRef) => formatPermitStatus(value),
      renderHeader: () => <IconAndLabelColumnHeader icon={CheckCircle} label={Translate.get("Status")} />,
      display: "flex",
      renderCell: (params) => renderPermitStatus(params.value, params.row),
      // This valueGetter will allow us to sort the status column, without using sortComparator
      valueGetter: (value, row, column, apiRef) => permitStatusToSortableValue(row),
    },
    {
      field: "reviewIsDue",
      headerName: Translate.get("ReviewNeeded"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={PreviewIcon} label={Translate.get("ReviewNeeded")} />,
      type: "boolean",
      renderCell: (params) =>
        params.row.reviewIsDue ? (
          <div>
            <Tooltip title={Translate.get("ReviewNeeded")}>
              <PendingActionsIcon />
            </Tooltip>
          </div>
        ) : (
          <div></div>
        ),
    },
    {
      field: "reviewDate",
      headerName: Translate.get("ReviewDate"),
      disableColumnMenu: true,
      flex: 1,
      type: "date",
      renderHeader: () => <IconAndLabelColumnHeader icon={PreviewIcon} label={Translate.get("ReviewDate")} />,
      valueGetter: (value, row, column, apiRef) => (row.reviewDate ? new Date(row.reviewDate) : ""),
    },
    {
      field: "reviewer",
      headerName: Translate.get("Reviewer"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={PreviewIcon} label={Translate.get("Reviewer")} />,
      valueGetter: (value, row, column, apiRef) => row.reviewer,
    },
    {
      field: "custom1",
      headerName: Translate.get("Permit"),
      disableColumnMenu: true,
      flex: 0,
      sortable: false,
      filterable: false,
      disableExport: true,
      renderHeader: () => <div></div>,
      renderCell: (params) => (
        <Tooltip title={Translate.get("Download")}>
          <Button
            className="regularButtonWithIcon stdLightLoader"
            variant="outlined"
            loading={params.row.actorPermitId === loading}
            onClick={(event) => {
              event.stopPropagation();
              if (params.row.blobFile === null) {
                handleDownloadActorPermit(params.row.actorPermitId, params.row.actorName ?? actorName);
              } else {
                downloadCustomPermit(params.row.blobFile.blobDocumentId);
              }
            }}
          >
            <DownloadIcon />
          </Button>
        </Tooltip>
      ),
    },
    {
      field: "dotview",
      headerName: "Menu",
      disableColumnMenu: true,
      flex: 0,
      maxWidth: 50,
      align: "right",
      sortable: false,
      filterable: false,
      disableExport: true,
      renderHeader: () => <div></div>,
      display: "flex",
      renderCell: (params) => (
        <DotMenu
          content={getDotMenuActions({
            actorPermit: params.row,
            onInactivateActorPermit: setActorPermitToInactivate,
            onDeleteActorPermitPermit: setActorPermitIdToDelete,
            onSendPermitSignReminder: handleSendNextPermitSignEmail,
            handleMarkPermitAsReviewed: handleMarkPermitAsReviewed,
          })}
          horizontal
        />
      ),
    },
    {
      field: "validTo",
      headerName: "Tillstånd giltigt t.o.m",
      disableExport: true,
      disableColumnMenu: true,
    },
    {
      field: "responsibleActorId",
      headerName: "Systemstatus: Id Ansvarig",
      sortable: false,
      disableExport: true,
      disableColumnMenu: true,
    },
    {
      field: "actorId",
      headerName: "Systemstatus: Id Person",
      sortable: false,
      disableExport: true,
      disableColumnMenu: true,
    },
    {
      field: "fActorPermit",
      headerName: "Systemstatus: Tillstånd snart utgående",
      sortable: false,
      disableExport: true,
      disableColumnMenu: true,
      valueGetter: (value, row, column, apiRef) => {
        const fdate = formatDateWithMonthOffSet(new Date(), 3);
        let chk = 0;
        if (row.validTo) {
          if (row.validTo < formatDateRange(fdate) && row.validTo > formatDateRange(new Date())) {
            chk = 1;
          }
        }
        return chk;
      },
    },
    {
      field: "permitNotSigned",
      headerName: "Systemstatus: Ej signerat",
      sortable: false,
      disableExport: true,
      disableColumnMenu: true,
      valueGetter: (value, row, column, apiRef) => {
        let chk = 0;
        const chkArr = [-1, 6];
        if ((!row.signatureId || !row.responsibleSignatureId) && !chkArr.includes(permitStatusToSortableValue(row))) {
          chk = 1;
        }
        return chk;
      },
    },
  ];

  if (!cardView && !isSmallScreen) {
    columns.unshift({
      field: "actorName",
      headerName: Translate.get("ActorName"),
      disableColumnMenu: true,
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={PersonIcon} label={Translate.get("ActorName")} />,
      display: "flex",
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <TableAvatar2
            image={params.row.actorPictureUrl}
            name={params.row.actorName}
            email={params.row.actorEmail}
            orgNo={params.row.actorOrgNo}
            externalActorNo={params.row.externalActorNo}
          />
        </div>
      ),
    });
  }

  const hiddenFields = [
    "responsibleActorId",
    "dotview",
    "permitNotSigned",
    "fActorPermit",
    "actorId",
    "validTo",
    "Menu",
    "parentActorName",
    "orgNo",
    "externalActorNo",
    "responsibleTitle",
  ];

  const getTogglableColumns = (columns) =>
    columns.filter((column) => !hiddenFields.includes(column.field)).map((column) => column.field);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    responsibleActorId: false,
    permitNotSigned: false,
    fActorPermit: false,
    actorId: false,
    validTo: false,
    reviewIsDue: false,
    reviewDate: false,
    reviewer: false,
    parentActorName: false,
    orgNo: false,
    externalActorNo: false,
    responsibleTitle: false,
  });

  // Toolbar CSS
  let toolbarCss = "stdVisible dgDynIconText";
  if (isSmallScreen) {
    toolbarCss = "stdVisible";
  }
  if (cardView) {
    toolbarCss += " dgNoFilter";
  }

  return (
    <>
      {!holderActorId && (
        <Box paddingTop={2} paddingLeft={1} paddingBottom={1} paddingRight={1}>
          <DataGridEasyFilters
            filters={easyFilters}
            currentFilterModel={pagination.filterModel}
            onSelectFilter={(filter) => {
              pagination.onFilterModelChange(filter?.filterModel);
              pagination.onPaginationModelChange({
                ...pagination.paginationModel,
                page: 0,
              });
            }}
            selectedFilter={selectedFilter}
          />
        </Box>
      )}

      <DataGridPremium
        showToolbar
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
        className={toolbarCss}
        columns={columns}
        disableRowSelectionOnClick
        getRowId={(row) => row.actorPermitId}
        localeText={localizedTextsMap}
        rowHeight={isSmallScreen ? 530 : 95}
        apiRef={apiRef}
        slots={{
          toolbar: () =>
            ToolBar({
              hideFilter: true,
              showExport: true,
              exportFileName: Translate.get("ActorPermit"),
              apiRef: apiRef,
              wide: true,
            }),
          noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
          noResultsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
        }}
        {...(!isSmallScreen && !cardView
          ? {
              onRowClick: (params) => {
                navigateToActor(params.row.actorId);
              },
            }
          : {})}
        slotProps={{
          columnsPanel: {
            getTogglableColumns,
          },
        }}
        disableDensitySelector
        disableColumnSelector={isSmallScreen}
        listView={isSmallScreen}
        listViewColumn={isSmallScreen ? listViewColDef : undefined}
        // For some reason if I did {...pagination} then filters wouldn't work.
        // As soon as I set a filter it'd switch back to defaults. But adding
        // the props individually worked. It had something to do with pageSizeOptions
        // it seemed. If the first page was 12 or smaller it worked, but 13 or above did not
        filterMode={pagination.filterMode}
        sortingMode={pagination.sortingMode}
        paginationMode={pagination.paginationMode}
        filterModel={pagination.filterModel}
        sortModel={pagination.sortModel}
        onFilterModelChange={pagination.onFilterModelChange}
        onSortModelChange={pagination.onSortModelChange}
        onPaginationModelChange={pagination.onPaginationModelChange}
        rows={pagination.rows}
        rowCount={pagination.rowCount}
        loading={pagination.isLoading}
        pagination={pagination.pagination}
        pageSizeOptions={pagination.pageSizeOptions}
        paginationModel={pagination.paginationModel}
      />
      <YesOrNoDialog
        open={!!actorPermitIdToDelete}
        title={Translate.get("DeletePermit")}
        text={Translate.get("SureDeletePermit")}
        onNo={() => setActorPermitIdToDelete(null)}
        onYes={() => {
          handleDeleteActorPermit();
          setActorPermitIdToDelete(null);
        }}
        noText={Translate.get("Cancel")}
        yesText={Translate.get("Ok")}
      />
      {actorPermitToInactivate && (
        <InactivateActorPermitDialog
          actorPermit={actorPermitToInactivate}
          onInactivateClick={handleInactivateActorPermit}
          onClose={() => setActorPermitToInactivate(null)}
        />
      )}
    </>
  );
};

export default Permits;
